import type {
  AccountApi,
  AccountTrip,
  AccountTripPage,
  AccountTripQuery,
  AccountTripUpdate,
  AuthSession,
  LoginCredentials,
  Pal,
  RegistrationDetails,
} from './accountTypes'
import { AccountApiError, MAX_PROFILE_IMAGE_BYTES, PROFILE_IMAGE_TYPES } from './accountTypes'
import type { TripDetails, TripPosition } from './types'

type JsonRecord = Record<string, unknown>
type PasswordEncryptor = (publicKeyXml: string, password: string) => Promise<string>

const defaultEndpoint = 'https://www.mapapal.com/Com/Com.aspx'

function record(value: unknown): JsonRecord | null {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
    ? value as JsonRecord
    : null
}

function oneOrMany(value: unknown): JsonRecord[] {
  if (Array.isArray(value)) return value.map(record).filter((item): item is JsonRecord => item !== null)
  const item = record(value)
  return item ? [item] : []
}

function text(value: unknown): string {
  return value == null ? '' : String(value)
}

function number(value: unknown, fallback = 0): number {
  const parsed = Number.parseFloat(text(value).replace(',', '.'))
  return Number.isFinite(parsed) ? parsed : fallback
}

function base64Url(value: string): string {
  return value.replaceAll('+', '-').replaceAll('/', '_').replaceAll('=', '')
}

function legacySessionParameter(value: string): string {
  return /%[0-9a-f]{2}/i.test(value) ? value : encodeURIComponent(value)
}

function xmlValue(xml: string, name: string): string {
  const match = xml.match(new RegExp(`<${name}>([^<]+)</${name}>`, 'i'))
  if (!match?.[1]) throw new AccountApiError('invalid-response', 'The login key was invalid.')
  return match[1]
}

export async function encryptLegacyPassword(
  publicKeyXml: string,
  password: string,
): Promise<string> {
  try {
    const key = await window.crypto.subtle.importKey(
      'jwk',
      {
        kty: 'RSA',
        n: base64Url(xmlValue(publicKeyXml, 'Modulus')),
        e: base64Url(xmlValue(publicKeyXml, 'Exponent')),
        alg: 'RSA-OAEP',
        ext: true,
      },
      { name: 'RSA-OAEP', hash: 'SHA-1' },
      false,
      ['encrypt'],
    )
    const encrypted = await window.crypto.subtle.encrypt(
      { name: 'RSA-OAEP' },
      key,
      new TextEncoder().encode(password),
    )
    const bytes = new Uint8Array(encrypted)
    let binary = ''
    for (const byte of bytes) binary += String.fromCharCode(byte)
    return window.btoa(binary)
  } catch (error) {
    if (error instanceof AccountApiError) throw error
    throw new AccountApiError('invalid-response', 'Secure login is unavailable in this browser.', { cause: error })
  }
}

function normalizeStatus(value: unknown): AccountTrip['status'] {
  const candidate = text(value)
  return candidate === 'running' || candidate === 'paused' ? candidate : 'ended'
}

function normalizeVisibility(value: unknown): AccountTrip['visibility'] {
  if (text(value) === '0') return 'private'
  if (text(value) === '1' || text(value) === '2') return 'pals'
  if (text(value) === '3') return 'public'
  return 'unknown'
}

function shareToken(value: JsonRecord): string | undefined {
  const direct = text(value.uno).trim()
  if (direct) return direct
  const privateLink = text(value.privatelink).trim()
  if (!privateLink) return undefined
  try {
    return new URL(privateLink, 'https://www.mapapal.com/').searchParams.get('uno')?.trim() || undefined
  } catch {
    return undefined
  }
}

function normalizeTrip(value: JsonRecord): AccountTrip | null {
  const id = text(value.trip_id)
  if (!id) return null
  const views = number(value.views, Number.NaN)
  return {
    id,
    shareToken: shareToken(value),
    ownerName: text(value.username) || 'Mapapal user',
    description: text(value.desc) || undefined,
    status: normalizeStatus(value.status),
    visibility: normalizeVisibility(value.sharestatus_id),
    startedAt: text(value.t_start),
    endedAt: text(value.t_stop) || undefined,
    durationSeconds: number(value.tot_s),
    distanceKilometers: number(value.dist_km),
    averageSpeedKilometersPerHour: number(value.avgkmh),
    platform: text(value.platform).trim() || undefined,
    views: Number.isFinite(views) ? Math.max(0, Math.trunc(views)) : undefined,
  }
}

function normalizePal(value: JsonRecord): Pal | null {
  const username = text(value.name).trim()
  if (!username) return null
  return { id: text(value.id).trim(), username }
}

function normalizePosition(value: JsonRecord): TripPosition | null {
  const latitude = number(value.lat, Number.NaN)
  const longitude = number(value.lng, Number.NaN)
  if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return null

  const optional = (input: unknown) => {
    const parsed = number(input, Number.NaN)
    return Number.isFinite(parsed) ? parsed : undefined
  }

  return {
    id: text(value.DBID),
    latitude,
    longitude,
    recordedAt: text(value.t_added),
    altitudeMeters: optional(value.alt),
    accuracyMeters: optional(value.acc),
    speedMetersPerSecond: optional(value.spe),
    bearingDegrees: optional(value.bea),
    distanceKilometers: optional(value.tot_km),
    comment: text(value.c).trim() || undefined,
  }
}

function positionsFromPayload(payload: JsonRecord, allowNoPositions = false): TripPosition[] {
  const ret = record(payload.ret)
  if (text(ret?.status) !== 'ok') {
    if (allowNoPositions && /no positions/i.test(text(ret?.msg))) return []
    throw new AccountApiError(
      'invalid-response',
      text(ret?.msg) || 'The route could not be loaded.',
    )
  }
  const positions = record(ret?.positions)
  return oneOrMany(positions?.pos)
    .map(normalizePosition)
    .filter((position): position is TripPosition => position !== null)
}

export interface LegacyAccountApiOptions {
  baseUrl?: string
  fetchImpl?: typeof fetch
  passwordEncryptor?: PasswordEncryptor
}

export class LegacyAccountApi implements AccountApi {
  private readonly endpoint: string
  private readonly fetchImpl: typeof fetch
  private readonly passwordEncryptor: PasswordEncryptor

  constructor(options: LegacyAccountApiOptions = {}) {
    this.endpoint = options.baseUrl ?? defaultEndpoint
    this.fetchImpl = options.fetchImpl ?? ((input, init) => fetch(input, init))
    this.passwordEncryptor = options.passwordEncryptor ?? encryptLegacyPassword
  }

  async login(credentials: LoginCredentials, signal?: AbortSignal): Promise<AuthSession> {
    const encryptedPassword = await this.encryptPassword(credentials.password, signal)
    const payload = await this.request({
      sid: '',
      f: 'user_authenticate',
      u: credentials.username,
      p_crypt: encryptedPassword,
    }, signal)
    return this.normalizeSession(payload, 'invalid-credentials')
  }

  async register(details: RegistrationDetails, signal?: AbortSignal): Promise<void> {
    const encryptedPassword = await this.encryptPassword(details.password, signal)
    const payload = await this.request({
      sid: '',
      f: 'user_register',
      app: 'mapapal',
      r_u: details.username,
      r_e: details.email,
      r_p_crypt: encryptedPassword,
    }, signal)
    this.requireOk(payload, 'Your account could not be created.')
  }

  async requestPasswordReset(usernameOrEmail: string, signal?: AbortSignal): Promise<void> {
    const payload = await this.request({
      sid: '',
      f: 'user_resetpassword',
      reset_ue: usernameOrEmail,
      app: 'mapapal',
    }, signal)
    this.requireOk(payload, 'The reset request could not be completed.')
  }

  async validateSession(session: AuthSession, signal?: AbortSignal): Promise<AuthSession> {
    const payload = await this.request({ sid: session.sid, f: 'user_authenticate' }, signal)
    return this.normalizeSession(payload, 'expired-session')
  }

  async listOwnTrips(session: AuthSession, query: AccountTripQuery = {}, signal?: AbortSignal): Promise<AccountTripPage> {
    const limit = query.limit ?? 15
    const filters = query.filters
    const viewMask = filters
      ? `${filters.includeOwn ? '1' : '0'}${filters.includePals ? '1' : '0'}${filters.includePublic ? '1' : '0'}1`
      : '1111'
    const payload = await this.request({
      sid: session.sid,
      f: 'trip_list',
      v_m: viewMask,
      rc: String(limit),
      ...(filters?.freeText.trim() ? { fritext: filters.freeText.trim() } : {}),
      ...(filters?.fromDate ? { f_t: filters.fromDate } : {}),
      ...(filters?.toDate ? { t_t: filters.toDate } : {}),
      ...(query.beforeTripId ? { first_id: query.beforeTripId } : {}),
    }, signal)
    const ret = record(payload.ret)
    if (text(ret?.status) === 'error') {
      throw new AccountApiError(
        'invalid-response',
        text(ret?.msg) || 'Mapapal could not return your trips.',
      )
    }
    const trips = record(ret?.trips)
    const normalized = oneOrMany(trips?.trip)
      .map(normalizeTrip)
      .filter((trip): trip is AccountTrip => trip !== null)
    return { trips: normalized, hasEarlier: normalized.length >= limit }
  }

  async getTrip(
    session: AuthSession,
    trip: AccountTrip,
    signal?: AbortSignal,
  ): Promise<TripDetails> {
    const payload = await this.request({
      sid: session.sid,
      f: 'trip_getpositions',
      t_id: trip.id,
    }, signal)
    return {
      ...trip,
      positions: positionsFromPayload(payload),
    }
  }

  async refreshTrip(
    session: AuthSession,
    tripId: string,
    lastPositionId: string,
    signal?: AbortSignal,
  ): Promise<TripDetails> {
    const [tripPayload, positionsPayload] = await Promise.all([
      this.request({ sid: session.sid, f: 'trip_get', t_id: tripId }, signal),
      this.request({
        sid: session.sid,
        f: 'trip_getpositions',
        t_id: tripId,
        last_id: lastPositionId || '0',
      }, signal),
    ])
    const tripRet = record(tripPayload.ret)
    const trips = record(tripRet?.trips)
    const trip = oneOrMany(trips?.trip).find((item) => text(item.trip_id) === tripId)
    if (text(tripRet?.status) !== 'ok' || !trip) {
      throw new AccountApiError('invalid-response', text(tripRet?.msg) || 'The trip could not be updated.')
    }
    const normalized = normalizeTrip(trip)
    if (!normalized) throw new AccountApiError('invalid-response', 'The trip could not be updated.')
    return {
      ...normalized,
      positions: positionsFromPayload(positionsPayload, true),
    }
  }

  async getTripCsv(session: AuthSession, tripId: string, signal?: AbortSignal): Promise<Blob> {
    const query = new URLSearchParams({ f: 'trip_save', t_id: tripId })
    const url = `${this.endpoint}?web&sid=${legacySessionParameter(session.sid)}&${query}`
    let response: Response
    try {
      response = await this.fetchImpl(url, {
        method: 'GET',
        headers: { Accept: 'text/csv,application/octet-stream' },
        signal,
      })
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') throw error
      throw new AccountApiError('network', 'Mapapal could not be reached.', { cause: error })
    }
    if (!response.ok) {
      throw new AccountApiError('network', `Mapapal returned HTTP ${response.status}.`)
    }
    const blob = await response.blob()
    if (blob.size === 0) throw new AccountApiError('invalid-response', 'Mapapal returned an empty CSV file.')
    return blob
  }

  async listPals(session: AuthSession, invert = false, signal?: AbortSignal): Promise<Pal[]> {
    const payload = await this.request({
      sid: session.sid,
      f: 'user_pals_list',
      ...(invert ? { invert: '1' } : {}),
    }, signal)
    const ret = record(payload.ret)
    if (text(ret?.status) !== 'ok') {
      throw new AccountApiError('request-rejected', text(ret?.msg) || 'Your pals could not be loaded.')
    }
    const pals = record(ret?.pals)
    return oneOrMany(pals?.pal).map(normalizePal).filter((pal): pal is Pal => pal !== null)
  }

  async addPal(session: AuthSession, usernameOrEmail: string, signal?: AbortSignal): Promise<string | undefined> {
    const payload = await this.request({
      sid: session.sid,
      f: 'user_pals_add',
      p_u: usernameOrEmail.trim(),
    }, signal)
    this.requireOk(payload, 'The pal could not be added.')
    const message = text(record(payload.ret)?.msg).trim()
    return message || undefined
  }

  async removePal(session: AuthSession, username: string, signal?: AbortSignal): Promise<void> {
    const payload = await this.request({
      sid: session.sid,
      f: 'user_pals_remove',
      p_u: username,
    }, signal)
    this.requireOk(payload, 'The pal could not be removed.')
  }

  async uploadProfileImage(session: AuthSession, file: File, signal?: AbortSignal): Promise<void> {
    if (file.size > MAX_PROFILE_IMAGE_BYTES) {
      throw new AccountApiError('request-rejected', 'The image must be 2 MB or smaller.')
    }
    if (!PROFILE_IMAGE_TYPES.includes(file.type as (typeof PROFILE_IMAGE_TYPES)[number])) {
      throw new AccountApiError('request-rejected', 'Choose a JPG or PNG image.')
    }

    const body = new FormData()
    body.append('file', file, file.name)
    body.append('action', 'upload')
    const url = `${this.endpoint}?web&sid=${legacySessionParameter(session.sid)}&f=user_image`
    let response: Response
    try {
      response = await this.fetchImpl(url, {
        method: 'POST',
        headers: { Accept: 'application/json,text/plain,*/*' },
        body,
        signal,
      })
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') throw error
      throw new AccountApiError('network', 'Mapapal could not be reached.', { cause: error })
    }
    if (!response.ok) {
      throw new AccountApiError('network', `Mapapal returned HTTP ${response.status}.`)
    }

    const responseText = (await response.text()).trim()
    if (!responseText) return
    try {
      const payload = JSON.parse(responseText) as JsonRecord
      const ret = record(payload.ret)
      if (ret && text(ret.status) !== 'ok') {
        throw new AccountApiError('request-rejected', text(ret.msg) || 'The profile image could not be uploaded.')
      }
    } catch (error) {
      if (error instanceof AccountApiError) throw error
      if (response.headers.get('Content-Type')?.includes('json')) {
        throw new AccountApiError('invalid-response', 'Mapapal returned invalid data.', { cause: error })
      }
      // The legacy endpoint may return a plain-text success response.
    }
  }

  async updateTrip(
    session: AuthSession,
    tripId: string,
    update: AccountTripUpdate,
    signal?: AbortSignal,
  ): Promise<void> {
    const visibility = { private: '0', pals: '2', public: '3' }[update.visibility]
    const payload = await this.request({
      sid: session.sid,
      f: 'trip_update',
      t_id: tripId,
      desc: update.description,
      share: visibility,
    }, signal)
    this.requireOk(payload, 'The trip could not be updated.')
  }

  async deleteTrip(session: AuthSession, tripId: string, signal?: AbortSignal): Promise<void> {
    const payload = await this.request({
      sid: session.sid,
      f: 'trip_delete',
      t_id: tripId,
    }, signal)
    this.requireOk(payload, 'The trip could not be deleted.')
  }

  async endTrip(session: AuthSession, tripId: string, signal?: AbortSignal): Promise<void> {
    const payload = await this.request({
      sid: session.sid,
      f: 'trip_end',
      t_id: tripId,
    }, signal)
    this.requireOk(payload, 'The trip could not be ended.')
  }

  private normalizeSession(
    payload: JsonRecord,
    failureCode: 'invalid-credentials' | 'expired-session',
  ): AuthSession {
    const ret = record(payload.ret)
    if (text(ret?.status) !== 'ok') {
      throw new AccountApiError(failureCode, failureCode === 'invalid-credentials'
        ? 'The username or password was incorrect.'
        : 'Your session has expired.')
    }
    const sid = text(ret?.sid)
    const username = text(ret?.username)
    if (!sid || !username) {
      throw new AccountApiError('invalid-response', 'Mapapal returned an incomplete session.')
    }
    return { sid, username, email: text(ret?.email) || undefined }
  }

  private async encryptPassword(password: string, signal?: AbortSignal): Promise<string> {
    const keyPayload = await this.request({ sid: '', f: 'publickey' }, signal)
    const keyRet = record(keyPayload.ret)
    const encodedKey = text(keyRet?.key)
    if (text(keyRet?.status) !== 'ok' || !encodedKey) {
      throw new AccountApiError('invalid-response', 'Mapapal did not return a login key.')
    }
    return this.passwordEncryptor(decodeURIComponent(encodedKey), password)
  }

  private requireOk(payload: JsonRecord, fallbackMessage: string): void {
    const ret = record(payload.ret)
    if (text(ret?.status) !== 'ok') {
      throw new AccountApiError('request-rejected', text(ret?.msg) || fallbackMessage)
    }
  }

  private async request(params: Record<string, string>, signal?: AbortSignal): Promise<JsonRecord> {
    const { sid = '', ...requestParams } = params
    const query = new URLSearchParams(requestParams)
    const url = `${this.endpoint}?mobweb&sid=${legacySessionParameter(sid)}&${query}`
    let response: Response
    try {
      response = await this.fetchImpl(url, {
        method: 'GET',
        headers: { Accept: 'application/json' },
        signal,
      })
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') throw error
      throw new AccountApiError('network', 'Mapapal could not be reached.', { cause: error })
    }
    if (!response.ok) {
      throw new AccountApiError('network', `Mapapal returned HTTP ${response.status}.`)
    }
    try {
      return await response.json() as JsonRecord
    } catch (error) {
      throw new AccountApiError('invalid-response', 'Mapapal returned invalid data.', { cause: error })
    }
  }
}
